import { memo, useState } from "react";
import { RedoModal } from "../arbiter-components/modal/redo-modal";
import { Text } from "../text";
import {
  AddressSelection,
  AddressSelectionRadioGroup,
  toAddressFields,
} from "./address-suggestion";

export interface AddressCore {
  street1: string;
  street2?: string | null;
  city: string;
  state?: string | null;
  zip: string;
  country: string;
}

function computeChangedFields(
  original: AddressCore,
  suggestion: AddressCore,
): Partial<
  Record<"street1" | "street2" | "city" | "state" | "zip" | "country", boolean>
> {
  const differs = (a?: string | null, b?: string | null) =>
    (a ?? "") !== (b ?? "");
  return {
    street1: differs(original.street1, suggestion.street1),
    street2: differs(original.street2, suggestion.street2),
    city: differs(original.city, suggestion.city),
    state: differs(original.state, suggestion.state),
    zip: differs(original.zip, suggestion.zip),
    country: differs(original.country, suggestion.country),
  };
}

export const AddressVerificationModal = memo(function AddressVerificationModal<
  T extends AddressCore,
>({
  address,
  suggestion,
  hasVerificationError,
  blockOnError = false,
  onClose,
  onSave,
}: {
  address: T;
  suggestion: AddressCore | undefined;
  hasVerificationError: boolean;
  /** When true, hides the Continue button on error so the user must fix the address. */
  blockOnError?: boolean;
  onClose(): void;
  onSave(address: T): void;
}) {
  const [selected, setSelected] = useState<AddressSelection>(
    suggestion ? "suggested" : "original",
  );

  const isOpen = !!suggestion || hasVerificationError;

  if (!isOpen) {
    return null;
  }

  const originalFields = toAddressFields(address);
  const suggestionFields = suggestion ? toAddressFields(suggestion) : undefined;
  const changedFields =
    suggestion && !hasVerificationError
      ? computeChangedFields(address, suggestion)
      : undefined;

  const handleConfirm = () => {
    if (selected === "suggested" && suggestion) {
      onSave({
        ...address,
        street1: suggestion.street1,
        street2: suggestion.street2,
        city: suggestion.city,
        state: suggestion.state,
        zip: suggestion.zip,
        country: suggestion.country,
      } as T);
    } else {
      onSave(address);
    }
  };

  const isHardBlocked = blockOnError && hasVerificationError;

  return (
    <RedoModal
      isOpen
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={
        isHardBlocked ? undefined : { text: "Continue", onClick: handleConfirm }
      }
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      subtitle={
        hasVerificationError && !suggestion
          ? "We were unable to verify this address."
          : "We found a more accurate version of this address."
      }
      title="Address verification"
    >
      <AddressSelectionRadioGroup
        changedFields={changedFields}
        disabled={hasVerificationError}
        errorContent={
          <Text fontSize="sm" textColor="error">
            {isHardBlocked
              ? "This address could not be verified. Please go back and correct it before saving."
              : "We were unable to verify this address. Please double check it or continue if you know the address is correct."}
          </Text>
        }
        hasError={hasVerificationError && !suggestion}
        onSelect={setSelected}
        originalFields={originalFields}
        selected={selected}
        suggestedAddress={suggestionFields}
      />
    </RedoModal>
  );
}) as <T extends AddressCore>(props: {
  address: T;
  suggestion: AddressCore | undefined;
  hasVerificationError: boolean;
  onClose(): void;
  onSave(address: T): void;
  blockOnError?: boolean;
}) => JSX.Element | null;
